import { Link } from "react-router-dom";
import { Instagram, MessageCircle, Phone, Mail, MapPin, Radio, Navigation } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { directionsUrl } from "@/lib/format";
import { BusinessName } from "./common";

export default function Footer() {
  const { business, settingsLoading } = useStore();
  const b = business || {};
  const cols = [
    { title: "Shop", links: [["All Products", "/shop"], ["Categories", "/categories"], ["Brands", "/brands"], ["Best Deals", "/shop?sort=discount"]] },
    { title: "Customer", links: [["My Account", "/account"], ["My Orders", "/account/orders"], ["Wishlist", "/wishlist"], ["Track Order", "/track-order"]] },
    { title: "Information", links: [["About Us", "/about"], ["Our Store", "/store"], ["Services", "/services"], ["Contact", "/contact"]] },
    { title: "Policies", links: [["Privacy Policy", "/privacy-policy"], ["Terms", "/terms"], ["Shipping", "/shipping-policy"], ["Returns & Refunds", "/return-refund-policy"], ["Cancellation", "/cancellation-policy"]] },
  ];

  return (
    <footer className="bg-slate-900 text-slate-300 mt-16" data-testid="footer">
      <div className="container-x py-12 grid grid-cols-2 md:grid-cols-6 gap-8">
        <div className="col-span-2">
          <Link to="/" className="flex items-center gap-2 mb-3">
            <span className="h-9 w-9 rounded-lg bg-white text-slate-900 grid place-items-center font-display font-bold">S</span>
            <span className="font-display font-bold text-lg text-white"><BusinessName name={b.name} loading={settingsLoading} /></span>
          </Link>
          <p className="text-sm text-slate-400 leading-relaxed max-w-xs">{b.tagline || "Genuine gadgets & accessories, delivered."}</p>
          <div className="mt-4 space-y-2 text-sm text-slate-400">
            {b.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-amber-400" /> {b.phone}</div>}
            {b.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-amber-400" /> {b.email}</div>}
            {(b.address || b.city) && <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-amber-400 mt-0.5" /> <span>{[b.address, b.city, b.state, b.pincode].filter(Boolean).join(", ")}</span></div>}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {b.instagram && <a href={b.instagram} target="_blank" rel="noreferrer" className="p-2 rounded-lg bg-slate-800 hover:bg-amber-500 transition" title="Instagram" data-testid="footer-instagram"><Instagram className="h-4 w-4" /></a>}
            {b.whatsappChannel && <a href={b.whatsappChannel} target="_blank" rel="noreferrer" className="p-2 rounded-lg bg-slate-800 hover:bg-green-600 transition" title="WhatsApp Channel" data-testid="footer-whatsapp-channel"><MessageCircle className="h-4 w-4" /></a>}
            {b.instagramChannel && <a href={b.instagramChannel} target="_blank" rel="noreferrer" className="p-2 rounded-lg bg-slate-800 hover:bg-fuchsia-500 transition" title="Instagram Channel" data-testid="footer-instagram-channel"><Radio className="h-4 w-4" /></a>}
            <a href={directionsUrl(b)} target="_blank" rel="noreferrer" className="p-2 rounded-lg bg-slate-800 hover:bg-amber-500 transition" title="Get Directions" data-testid="footer-directions"><Navigation className="h-4 w-4" /></a>
          </div>
        </div>
        {cols.map((col) => (
          <div key={col.title}>
            <h4 className="font-semibold text-white text-sm mb-3">{col.title}</h4>
            <ul className="space-y-2 text-sm">
              {col.links.map(([l, to]) => (
                <li key={to}><Link to={to} className="text-slate-400 hover:text-amber-400 transition">{l}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-800">
        <div className="container-x py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <p>© {new Date().getFullYear()}{(b.legalName || b.name) ? ` ${b.legalName || b.name}.` : ""} All rights reserved.</p>
          <p>Secure payments • Reliable delivery • Genuine products</p>
        </div>
      </div>
    </footer>
  );
}
